import { useLayoutEffect, useRef } from 'react';
import type { TextareaHTMLAttributes } from 'react';
import './auto-grow-textarea.css';

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'defaultValue'> & { value: string };

/** Soft wrapping preserves the answer; a hidden measuring field avoids collapsing
 * the focused field and moving the page while typing near the bottom of a phone. */
export default function AutoGrowTextarea({ value, rows = 1, className = '', ...props }: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const resizeRef = useRef<() => void>(() => {});

  useLayoutEffect(() => {
    const field = ref.current;
    if (!field) return;
    const mirror = document.createElement('textarea');
    mirror.tabIndex = -1;
    mirror.setAttribute('aria-hidden', 'true');
    mirror.style.cssText = 'position:fixed;left:-10000px;top:0;visibility:hidden;pointer-events:none;height:auto;max-height:none;overflow:hidden;resize:none;box-sizing:border-box;white-space:pre-wrap;overflow-wrap:break-word;word-break:break-word;';
    document.body.appendChild(mirror);
    let width = '', disposed = false;
    const resize = () => {
      if (disposed || !field.getBoundingClientRect().width) return;
      const css = getComputedStyle(field);
      width = css.width;
      for (const name of ['font-family', 'font-size', 'font-weight', 'font-style', 'line-height', 'letter-spacing', 'text-indent', 'text-transform', 'direction', 'padding-top', 'padding-bottom', 'padding-left', 'padding-right', 'border-top-width', 'border-bottom-width', 'border-left-width', 'border-right-width', 'border-style', 'min-height']) {
        mirror.style.setProperty(name, css.getPropertyValue(name));
      }
      mirror.style.width = width;
      mirror.rows = field.rows;
      mirror.value = field.value || field.placeholder || ' ';
      const borders = parseFloat(css.borderTopWidth) + parseFloat(css.borderBottomWidth);
      field.style.height = `${Math.ceil(mirror.scrollHeight + borders)}px`;
    };
    resizeRef.current = resize;
    resize();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
      if (getComputedStyle(field).width !== width) resize();
    });
    observer?.observe(field);
    // Older iPad Safari has no ResizeObserver. Rotation/window resize still works.
    window.addEventListener('resize', resize);
    document.fonts?.ready.then(resize);
    return () => {
      disposed = true;
      observer?.disconnect();
      window.removeEventListener('resize', resize);
      mirror.remove();
      resizeRef.current = () => {};
    };
  }, []);

  useLayoutEffect(() => { resizeRef.current(); }, [value, rows]);

  return <textarea {...props} ref={ref} value={value} rows={rows} wrap="soft" className={`${className} answer-auto-grow`} />;
}
